import React, { useState, useMemo, useCallback } from 'react';
import { CROPS, DATA } from '../game/data';
import {
  newGame, plant, advanceWeek, canPlant, score, cropById, seedCost,
  isOccupying, harvestStart, harvestEnd, inSowWindow, frostRisk,
  priceIndex, wk, monthOf, GameState, WeekResult, HOURS_PER_WORKER_WEEK,
} from '../game/engine';

// Playable game (added 25 Oct 2026). Route: /game/play.
// All mechanics come from src/game/engine.ts, a port of the Urvara simulation.

const money = (n: number) => (n < 0 ? '-£' : '£') + Math.abs(Math.round(n)).toLocaleString();

export default function GamePlay() {
  const [state, setState] = useState<GameState>(() => newGame());
  const [picked, setPicked] = useState<string | null>(null);
  const [last, setLast] = useState<WeekResult | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const sc = useMemo(() => score(state), [state]);
  const month = DATA.months[monthOf(state.week)];

  const say = useCallback((m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(t => (t === m ? null : t)), 2600);
  }, []);

  const step = useCallback((n: number) => {
    setState(s => {
      let cur = s, res: WeekResult | null = null;
      for (let i = 0; i < n; i++) { const r = advanceWeek(cur); cur = r.state; res = r.result; }
      setLast(res);
      return cur;
    });
  }, []);

  const onBed = (bedId: number) => {
    if (!picked) { say('Pick a crop first'); return; }
    const err = canPlant(state, bedId, picked);
    if (err) { say(err); return; }
    setState(s => plant(s, bedId, picked));
    setPicked(null);
  };

  const bedInfo = (bedId: number) => {
    const p = state.plantings.find(x => x.bed === bedId &&
      isOccupying(x, cropById(x.cropId)!, state.week));
    if (!p) return null;
    const c = cropById(p.cropId)!;
    const hs = harvestStart(p, c), he = harvestEnd(p, c);
    const harvesting = state.week >= hs && state.week <= he;
    const weeksLeft = Math.max(0, hs - state.week);
    return { p, c, harvesting, weeksLeft };
  };

  const plantable = CROPS.filter(c => inSowWindow(c, state.week));

  return (
    <div className="landing-root play-root">
      <header className="play-bar">
        <a href="/game" className="play-back">&larr; Game</a>
        <div className="play-stat"><span>Week</span><b>{state.week}</b></div>
        <div className="play-stat"><span>Month</span><b>{month}</b></div>
        <div className="play-stat"><span>Year</span><b>{state.year}</b></div>
        <div className="play-stat play-stat--cash"><span>Cash</span><b>{money(state.cash)}</b></div>
        <div className="play-scores">
          <span className="ps ps-profit" title="Profit">{Math.round(sc.profit)}</span>
          <span className="ps ps-planet" title="Planet">{Math.round(sc.planet)}</span>
          <span className="ps ps-people" title="People">{Math.round(sc.people)}</span>
          <span className="ps ps-total" title="Total — your weakest score counts twice">
            {Math.round(sc.total)}</span>
        </div>
      </header>

      <div className="play-wrap">
        {/* ── crops ── */}
        <aside className="play-crops">
          <h3>Sow this week</h3>
          {plantable.length === 0 && (
            <p className="play-empty">Nothing can be sown in week {state.week}. Advance the weeks.</p>
          )}
          {plantable.map(c => {
            // a crop is only "blocked" if NO bed can take it — checking just the
            // first empty bed wrongly dimmed tunnel crops while the tunnel was free
            const blocked = state.beds.every(b => canPlant(state, b.id, c.id) !== null);
            return (
              <button key={c.id} type="button"
                className={`play-crop ${picked === c.id ? 'on' : ''} ${blocked ? 'blocked' : ''}`}
                onClick={() => setPicked(picked === c.id ? null : c.id)}
                title={c.note}>
                <span className="pc-name">{c.name}{c.protected && <i className="pc-tun">tunnel</i>}</span>
                <span className="pc-meta">
                  {c.yieldKgM2} kg/m² · £{c.price.toFixed(2)} ·
                  {' '}ready in {c.prop + c.grow} wk ·
                  {' '}£{seedCost(c, 100)} seed
                </span>
                <span className="pc-price">
                  price now ×{priceIndex(c, state.week).toFixed(2)}
                </span>
              </button>
            );
          })}
        </aside>

        {/* ── beds ── */}
        <main className="play-main">
          <div className="play-beds">
            {state.beds.map(b => {
              const info = bedInfo(b.id);
              return (
                <button key={b.id} type="button"
                  className={`play-bed ${info ? (info.harvesting ? 'harvest' : 'growing') : 'empty'} ${b.protected ? 'tunnel' : ''}`}
                  onClick={() => onBed(b.id)}>
                  <span className="pb-head">
                    {b.protected ? 'Polytunnel' : `Bed ${b.id + 1}`}
                    <i>{b.areaM2} m²</i>
                  </span>
                  {info ? (
                    <>
                      <strong>{info.c.name}</strong>
                      <span className="pb-state">
                        {info.harvesting ? 'Harvesting now' : `Ready in ${info.weeksLeft} wk`}
                      </span>
                    </>
                  ) : (
                    <span className="pb-state pb-empty">
                      {picked ? 'Click to sow here' : 'Empty'}
                    </span>
                  )}
                  <span className="pb-som" title="Soil organic matter">
                    soil {b.som.toFixed(2)}%
                  </span>
                </button>
              );
            })}
          </div>

          <div className="play-controls">
            <button className="play-go" onClick={() => step(1)}>Advance 1 week</button>
            <button className="play-go play-go--alt" onClick={() => step(4)}>Advance 4 weeks</button>
            <button className="play-reset" onClick={() => { setState(newGame(Math.floor(Math.random() * 1e6))); setLast(null); setPicked(null); }}>
              New season
            </button>
          </div>

          {last && (
            <div className="play-result">
              <div className="pr-row">
                <span>Labour</span>
                <b className={last.overloaded ? 'bad' : ''}>
                  {last.labourHours.toFixed(0)} / {last.capacityHours} h
                </b>
              </div>
              <div className="pr-row"><span>Harvested</span><b>{last.kgSold.toFixed(0)} kg</b></div>
              {last.kgLost > 1 && (
                <div className="pr-row"><span>Unpicked</span><b className="bad">{last.kgLost.toFixed(0)} kg</b></div>
              )}
              <div className="pr-row"><span>Sold at</span>
                <b>{last.blended ? `×${last.blended.toFixed(2)} of list` : '—'}</b></div>
              <div className="pr-row"><span>Revenue</span><b className="good">{money(last.revenue)}</b></div>
              <div className="pr-row"><span>Wages</span><b>{money(-last.labourCost)}</b></div>
            </div>
          )}
        </main>

        {/* ── log ── */}
        <aside className="play-log">
          <h3>What happened</h3>
          <ul>
            {state.log.map((e, i) => (
              <li key={i} className={`pl-${e.severity}`}>
                <span className="pl-wk">wk {e.week}</span>{e.text}
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {toast && <div className="play-toast">{toast}</div>}

      <footer className="play-foot">
        Crop windows, prices, labour rates, soil behaviour and the channel ladder all come from the
        same model as the research. Figures are planning estimates and unverified.
        <a href="/game"> About the game</a>
      </footer>
    </div>
  );
}
